import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowDownRightIcon, FacebookIcon, MessageCircleIcon, TruckIcon } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { ProductDialog } from '../components/ProductDialog';
import { ReviewCard } from '../components/ReviewCard';
import { products } from '../data/products';
import { reviews } from '../data/reviews';
import { GENERAL_ENQUIRY_URL, SPECIFIC_FRAGRANCE_URL } from '../utils/whatsapp';
import type { Product } from '../types/catalog';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/012b1188-a520-46d8-9ceb-0efe7516bb47.jpg';
const HERO_IMAGE_MOBILE = 'https://cdn.magicpatterns.com/patterns/generated-images/4f09518b-fe98-4b27-bd24-223261549f59.jpg';
const marqueeItems = ['Envío gratis'];
const FACEBOOK_URL = 'https://www.facebook.com/profile.php?id=61592477107032';
const benefits = [{
  title: 'Dura hasta 10 horas',
  body: 'Concentraciones altas y frascos originales. Una pulsación por la mañana sigue ahí en la cena.'
}, {
  title: 'Ingredientes que reconoces',
  body: 'Casas de perfumería consolidadas, con la pirámide olfativa completa publicada en cada ficha.'
}, {
  title: 'Envío seguro y rastreable',
  body: 'Salimos en 24 horas con guía, seguro incluido y 30 días para devolver.'
}];
const easeSmooth = [0.23, 1, 0.32, 1] as const;
export function Home() {
  const [activeProduct, setActiveProduct] = useState<Product | null>(null);
  return <main id="inicio" className="bg-ivory" data-id="element-95" data-mp="2d7gttehncv8s">
      <section className="relative overflow-hidden" data-id="element-96" data-mp="2d5mcsb9hxn9z">
        <div className="mx-auto grid max-w-7xl items-center gap-8 px-5 pb-12 pt-12 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12 lg:pb-20 lg:pt-16" data-id="element-97" data-mp="287ksjn0c3hwx">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: easeSmooth
        }} className="max-w-xl" data-id="element-98" data-mp="23auef583awe5">
            

            <h1 className="mt-6 font-display text-[2.9rem] font-extrabold leading-[0.98] tracking-tight text-navy sm:text-6xl lg:text-7xl" data-id="element-99" data-mp="1kk7d2kx4oub0">
              No necesitas oler como{' '}
              <span className="text-cobalt" data-id="element-100" data-mp="1amsyyryoghlv">todos.</span>
            </h1>

            <p className="mt-6 max-w-lg font-display text-2xl font-bold leading-tight text-navy sm:text-3xl" data-id="element-101" data-mp="1aonoykot2zwt">
              Perfumes creados para que tu presencia hable antes que tú.
            </p>

            <a href="#perfumes" className="mt-8 inline-flex items-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-102" data-mp="23db9uf5n7n9u">
              Encuentra tu aroma
              <ArrowDownRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-103" data-mp="2machy8uhu3i0" />
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3,
          ease: easeSmooth,
          delay: 0.06
        }} data-id="element-104" data-mp="2r1ayn9je93ls">
            <img src={HERO_IMAGE_MOBILE} alt="Ilustración editorial de un frasco de perfume entre cítricos, pétalos y vapor" className="aspect-[4/5] w-full rounded-3xl object-cover lg:hidden" data-id="element-105" data-mp="akmvn8i9hdd9" />
            <img src={HERO_IMAGE} alt="Ilustración editorial de frascos de perfume entre cítricos, pétalos, anís y vapor" className="hidden aspect-[3/2] w-full rounded-3xl object-cover lg:block" data-id="element-106" data-mp="kdc0vln7xv05" />
          </motion.div>
        </div>
      </section>

      <div className="overflow-hidden bg-navy py-3" aria-hidden="true" data-id="element-107" data-mp="5o5wlj9ixele">
        <div className="marquee-track flex w-max gap-10 whitespace-nowrap" data-id="element-108" data-mp="ojht5e1rqah">
          {[...marqueeItems, ...marqueeItems, ...marqueeItems, ...marqueeItems].map((item, index) => <span key={`${item}-${index}`} className="font-display text-sm font-bold uppercase tracking-wide text-ivory" data-id="element-109" data-mp="1h0yjogrddev4" data-mpi={index}>
                {item} <span className="text-lime" data-id="element-110" data-mp="27dwlkqri3gyx">✦</span>
              </span>)}
        </div>
      </div>

      <section className="mx-auto max-w-5xl px-5 py-16 sm:px-8 lg:py-24" data-id="element-111" data-mp="27g98lic48hiu">
        <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl" data-id="element-112" data-mp="2cd83i33lssi3">
          ¿Cansado de perfumes que desaparecen
          <br className="hidden sm:block" data-id="element-113" data-mp="2mbfhcd2liufv" /> y huelen igual?
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-mute sm:text-lg" data-id="element-114" data-mp="2qd0rhqnqwumd">
          Uno entra a la sala antes que tú, otro se queda cerca y solo lo descubre quien se
          acerca.{' '}
          <span className="text-cobalt" data-id="element-115" data-mp="302u32rnw19qm">Elige el que quieras ser hoy.</span>
        </p>
      </section>

      <section id="perfumes" className="mx-auto max-w-7xl px-5 pb-8 sm:px-8" aria-labelledby="coleccion" data-id="element-116" data-mp="351dpsuab2pk1">
        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-line pt-8" data-id="element-117" data-mp="3a19tstj06gzl">
          <h2 id="coleccion" className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-id="element-118" data-mp="3e1ts40fbbqnb">
            Selección de fragancias
          </h2>
          <p className="inline-flex items-center gap-2 rounded-full bg-lime px-4 py-2 text-xs font-bold uppercase tracking-wide text-navy" data-id="element-119" data-mp="12cna3dzt5qe4">
            <TruckIcon className="h-4 w-4" aria-hidden="true" data-id="element-120" data-mp="tdid4c3wa542" />
            Envío totalmente gratis
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-5" data-id="element-121" data-mp="ogrxfpw05bj6">
          {products.map(product => <ProductCard key={product.slug} product={product} onOpen={setActiveProduct} data-id="element-122" data-mp="1m51x3y8bfn74" />)}
        </div>

        <div className="mt-14 rounded-3xl bg-navy px-6 py-10 sm:px-10 lg:py-12" data-id="element-123" data-mp="1h68lkbcla69l">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-124" data-mp="1cbbgffifkh1z">
            <div className="max-w-lg" data-id="element-125" data-mp="17dt3le1alhop">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-ivory sm:text-3xl" data-id="element-126" data-mp="24t6zb19br03t">
                ¿Buscas una fragancia específica?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-ivory/70 sm:text-base" data-id="element-127" data-mp="200nogc20uj2p">
                Cuéntanos cuál tienes en mente y consultamos disponibilidad para ti.
              </p>
            </div>
            <a href={SPECIFIC_FRAGRANCE_URL} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-128" data-mp="28mfzzlfvzti">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-129" data-mp="31mgepjwxm36k" />
              Consultar disponibilidad
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pt-16 sm:px-8 lg:pt-24" aria-labelledby="opiniones" data-id="element-130" data-mp="36muob6e6ll6p">
        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-line pt-8" data-id="element-131" data-mp="14u6jislw2pot">
          <h2 id="opiniones" className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl" data-id="element-132" data-mp="1ex21bmdui5q0">
            Opiniones de clientes
          </h2>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-mute" data-id="element-133" data-mp="1nqf55jdg6rqv">
            Compras verificadas
          </p>
        </div>

        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-134" data-mp="1ixvq0hz43ojy">
          {reviews.map(review => <li key={review.id} className="h-full" data-id="element-135" data-mp="1xmi4khgx0ca4">
              <ReviewCard review={review} data-id="element-136" data-mp="1sphy45g5l7ih" />
            </li>)}
        </ul>
      </section>

      <section className="mt-16 bg-navy lg:mt-24" aria-labelledby="beneficios" data-id="element-137" data-mp="26ohmdbzr7ke4">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:py-20" data-id="element-138" data-mp="26mezh0twe7dm">
          <h2 id="beneficios" className="sr-only" data-id="element-139" data-mp="22nnxogehm1k6">
            Por qué comprar en Jam Fragrances
          </h2>
          <ul className="grid gap-10 sm:grid-cols-3" data-id="element-140" data-mp="cb0526pxzjrj">
            {benefits.map((benefit, index) => <li key={benefit.title} data-id="element-141" data-mp="8apu0fnam35t" data-mpi={index}>
                <span className="font-display text-4xl font-extrabold text-lime" data-id="element-142" data-mp="wubgyi0kxrhh">
                  0{index + 1}
                </span>
                <h3 className="mt-3 font-display text-xl font-bold text-ivory" data-id="element-143" data-mp="11s4njc5sonia">{benefit.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ivory/70" data-id="element-144" data-mp="16kxrhxtfvrtb">{benefit.body}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-ivory" data-id="element-145" data-mp="dv7a9fwig9sz">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-5 py-16 sm:px-8 lg:flex-row lg:items-center lg:justify-between lg:py-20" data-id="element-146" data-mp="dw8y6kymkqs4">
          <h2 className="max-w-xl font-display text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-5xl" data-id="element-147" data-mp="iw52klb8ik87">
            Que te recuerden por algo.
          </h2>
          <div className="flex flex-wrap items-center gap-5" data-id="element-148" data-mp="rw9xvx35ls60">
            <a href={GENERAL_ENQUIRY_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-lime px-8 py-4 text-sm font-bold uppercase tracking-wide text-navy transition-colors duration-200 ease-smooth hover:bg-lime-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-cobalt" data-id="element-149" data-mp="1ybu2stzupoeh">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-150" data-mp="23nhd4xa1ft55" />
              Pedir por WhatsApp
            </a>
            <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-cobalt underline-offset-4 transition-colors duration-200 ease-smooth hover:underline" data-id="element-151" data-mp="1zm5wvid20k9m">
              <FacebookIcon className="h-4 w-4" aria-hidden="true" data-id="element-152" data-mp="1kpol4q0yd3mj" />
              Jam Fragrances
            </a>
          </div>
        </div>
      </section>

      <ProductDialog product={activeProduct} onClose={() => setActiveProduct(null)} data-id="element-153" data-mp="3f3pjl9slbom3" />
    </main>;
}